import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AskQuestionModel, ProfileVerificationRequiredData, ProfileVerificationField } from '../../../model/chat.model';
import { SharedService } from '../../../shared/services/shared.service';
import { UserSettingService } from '../../../services/user-setting.service';
import { ToasterService } from '../../../shared/services/toaster.service';
import { PhoneVerificationModalComponent } from '../../../shared/components/phone-verification-modal/phone-verification-modal.component';
import { EmailVerificationModalComponent } from '../../../shared/components/email-verification-modal/email-verification-modal.component';
import { User } from '../../../model/user.model';

@Component({
  selector: 'app-profile-verification-required',
  imports: [PhoneVerificationModalComponent, EmailVerificationModalComponent],
  templateUrl: './profile-verification-required.component.html',
  styleUrl: './profile-verification-required.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileVerificationRequiredComponent {
  private readonly _modalService = inject(NgbModal);
  private readonly _sharedService = inject(SharedService);
  private readonly _settingService = inject(UserSettingService);
  private readonly _toaster = inject(ToasterService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly data = input.required<ProfileVerificationRequiredData>();
  /** When false (stale chat message), buttons are disabled. */
  readonly interactive = input<boolean>(true);

  @ViewChild('verifyPhoneTemplate') verifyPhoneTpl!: TemplateRef<unknown>;
  @ViewChild('verifyEmailTemplate') verifyEmailTpl!: TemplateRef<unknown>;
  private verifyPhoneModalRef?: NgbModalRef;
  private verifyEmailModalRef?: NgbModalRef;

  /**
   * Verification status confirmed through this block in the current session.
   * We trust the payload for initial render and only flip these when the user
   * successfully completes verification via the modal here — avoids showing
   * "Verified" pills when the cached User happens to disagree with the BE.
   */
  private readonly phoneVerifiedLocally = signal(false);
  private readonly emailVerifiedLocally = signal(false);

  protected readonly phone = computed<ProfileVerificationField | undefined>(() => {
    const p = this.data().profile?.phone;
    if (!p) return undefined;
    return this.phoneVerifiedLocally() ? { ...p, verified: true } : p;
  });

  protected readonly email = computed<ProfileVerificationField | undefined>(() => {
    const e = this.data().profile?.email;
    if (!e) return undefined;
    return this.emailVerifiedLocally() ? { ...e, verified: true } : e;
  });

  /** True when a phone number is already on file (just unverified). */
  protected readonly hasPhoneNumber = computed(() => {
    const p = this.phone();
    return !!p && p.present !== false;
  });

  protected readonly needsPhone = computed(() => {
    const p = this.phone();
    return !!p && p.verified !== true;
  });

  /** Add mode (no number yet) → editable; verify mode (number on file) → locked. */
  protected readonly phoneEditable = computed(() => !this.hasPhoneNumber());

  protected readonly phoneCtaLabel = computed(() =>
    this.hasPhoneNumber() ? 'Verify phone' : 'Add phone number',
  );

  protected readonly phoneModalTitle = computed(() =>
    this.hasPhoneNumber() ? 'Verify your mobile' : 'Add your mobile',
  );

  protected readonly phoneModalDescription = computed(() =>
    this.hasPhoneNumber()
      ? "We'll send a one-time code to your phone to confirm it's yours so we can complete your booking."
      : '',
  );

  protected readonly needsEmail = computed(() => {
    const e = this.email();
    return !!e && e.present !== false && e.verified !== true;
  });

  protected readonly phoneDisplay = computed(() => {
    const p = this.phone();
    const cc = this.data().profile?.countryCode?.value;
    if (!p?.value) return '';
    return cc ? `${cc} ${p.value}` : p.value;
  });

  protected openPhoneVerify(): void {
    if (!this.interactive() || !this.needsPhone()) return;
    this.verifyPhoneModalRef = this._modalService.open(this.verifyPhoneTpl, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      windowClass: 'modal-md phone-verification-modal',
    });
  }

  protected onPhoneVerifyClosed(): void {
    this.verifyPhoneModalRef?.close();
    // refreshUserWithMedia() re-resolves the avatar URL and broadcasts the user;
    // using the raw getSetting() response here would blank the profile picture.
    this._settingService.refreshUserWithMedia()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (user: User) => {
          if (user.isPhoneVerified) {
            this.phoneVerifiedLocally.set(true);
          }
          this.maybeReplay(user);
        },
      });
  }

  protected openEmailVerify(): void {
    if (!this.interactive() || !this.needsEmail()) return;
    this.verifyEmailModalRef = this._modalService.open(this.verifyEmailTpl, {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      centered: true,
      windowClass: 'modal-md email-verification-modal',
    });
  }

  protected onEmailVerified(): void {
    this.verifyEmailModalRef?.close();
    this._toaster.success('Email verified.', 'Success');
    // refreshUserWithMedia() re-resolves the avatar URL and broadcasts the user;
    // using the raw getSetting() response here would blank the profile picture.
    this._settingService.refreshUserWithMedia()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (user: User) => {
          if (user.isEmailVerified) {
            this.emailVerifiedLocally.set(true);
          }
          this.maybeReplay(user);
        },
      });
  }

  protected onEmailModalClosed(): void {
    this.verifyEmailModalRef?.close();
  }

  /**
   * Replay only when every originally-required verification is now complete.
   * If something is still pending, nudge the user toward the remaining step
   * instead of firing the search prematurely.
   */
  private maybeReplay(user: User): void {
    const phoneStillPending = this.needsPhone() && !user.isPhoneVerified;
    const emailStillPending = this.needsEmail() && !user.isEmailVerified;
    if (!phoneStillPending && !emailStillPending) {
      this.replayResumeQuestion();
      return;
    }
    if (phoneStillPending) {
      this._toaster.info('Please verify your phone to continue.', 'One more step');
    } else if (emailStillPending) {
      this._toaster.info('Please verify your email to continue.', 'One more step');
    }
  }

  private replayResumeQuestion(): void {
    // Prefer the action the backend echoed back; otherwise reuse the last
    // tee-time action we cached from the original submission so the user
    // doesn't have to re-enter their search parameters.
    const cachedAction = this._sharedService.lastTeeTimeAction();
    const resumeAction = this.data().resumeAction ?? cachedAction;
    const question = this.data().resumeQuestion?.trim();
    if (!question && !resumeAction) return;
    const model = new AskQuestionModel();
    model.textQuestion = question ?? 'Search tee times';
    model.behaviour = 'clicked';
    model.showProcessingText = true;
    if (resumeAction) {
      model.action = resumeAction;
    }
    this._sharedService.setQuestion(model);
    // One-shot — clear the cache so a stale action can't accidentally fire later.
    this._sharedService.lastTeeTimeAction.set(undefined);
  }
}
